import { District, Location } from '../../types/schema';
import { tGroupedRegionWithDistricts, tGroupedDistrict } from '../../types';
import { Btn, BtnIconOnly, Card, IconEdit, IconPlus, IconTrash } from '../ui';
import { DistrictCard } from './DistrictCard';

interface RegionCardProps {
    region: tGroupedRegionWithDistricts;
    isExpanded: boolean;
    onToggle: () => void;
    onEditRegion: () => void;
    onDeleteRegion: () => void;
    onAddDistrict: () => void;
    onEditDistrict: (district: District) => void;
    onDeleteDistrict: (district: tGroupedDistrict) => void;
    onEditLocation: (location: Location) => void;
    onDeleteLocation: (location: Location) => void;
    onAddLocation: (name: string, districtId: string, regionalDivisionId: string) => void;
    getLocationEquipmentCount: (locationId: string) => number;
}

export const RegionCard = ({
    region,
    isExpanded,
    onToggle,
    onEditRegion,
    onDeleteRegion,
    onAddDistrict,
    onEditDistrict,
    onDeleteDistrict,
    onEditLocation,
    onDeleteLocation,
    onAddLocation,
    getLocationEquipmentCount,
}: RegionCardProps) => {
    const totalDistricts = region.districts.length;
    const totalLocations = region.districts.reduce((sum, d) => sum + d.locations.length, 0);
    const totalEquipment = region.districts.reduce(
        (sum, d) => sum + d.locations.reduce((inner, loc) => inner + getLocationEquipmentCount(loc.id), 0),
        0,
    );

    return (
        <Card variant="card-sm" cardClasses="region-card">
            {/* Region Header */}
            <div className="region-card-header" onClick={onToggle}>
                <div className="region-card-header-left">
                    <span className={`region-toggle ${isExpanded ? 'expanded' : ''}`}>▶</span>
                    <div>
                        <span className="region-badge">REGION</span>
                        <div className="region-card-info">
                            <h3>{region.name}</h3>
                            <span className="region-card-desc">
                                {region.description || `${totalDistricts} district${totalDistricts !== 1 ? 's' : ''}`}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="region-card-header-right">
                    <span className="region-stat">
                        <b>{totalDistricts}</b> Districts
                    </span>
                    <span className="region-stat">
                        <b>{totalLocations}</b> Locations
                    </span>
                    <span className="region-stat">
                        <b>{totalEquipment}</b> Equipment
                    </span>
                    <span onClick={(e) => e.stopPropagation()} role="presentation" className="table-actions">
                        <BtnIconOnly
                            size="sm"
                            icon={<IconEdit />}
                            ariaLabel={`Edit ${region.name}`}
                            onClick={onEditRegion}
                        />
                        <BtnIconOnly
                            size="sm"
                            variant="danger"
                            icon={<IconTrash />}
                            ariaLabel={`Delete ${region.name}`}
                            onClick={onDeleteRegion}
                        />
                    </span>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="region-card-body">
                    {region.districts.length === 0 ? (
                        <div className="region-empty-state">
                            <p id={`region-${region.id}-empty-title`}>No districts in this region</p>
                            <p id={`region-${region.id}-empty-hint`} className="region-empty-state-hint">
                                Add your first district to organize locations in {region.name}.
                            </p>
                            <Btn
                                variant="primary"
                                buttonSize="md"
                                buttonClasses="region-add-first-btn"
                                buttonIcon={<IconPlus />}
                                buttonText="Add First District"
                                buttonOnClick={onAddDistrict}
                                ariaLabel={`Add first district to ${region.name}`}
                                buttonAriaProps={{
                                    'aria-describedby': `region-${region.id}-empty-title region-${region.id}-empty-hint`,
                                }}
                            />
                        </div>
                    ) : (
                        <>
                            <div className="district-grid">
                                {region.districts.map((district) => (
                                    <DistrictCard
                                        key={district.id}
                                        district={district}
                                        regionId={region.id}
                                        onEdit={() => onEditDistrict(district)}
                                        onDelete={() => onDeleteDistrict(district)}
                                        onEditLocation={onEditLocation}
                                        onDeleteLocation={onDeleteLocation}
                                        onAddLocation={(name) => onAddLocation(name, district.id, region.id)}
                                        getLocationEquipmentCount={getLocationEquipmentCount}
                                    />
                                ))}
                            </div>
                            <Btn
                                variant="secondary-outlined"
                                buttonSize="md"
                                buttonClasses="region-add-district-btn"
                                buttonIcon={<IconPlus />}
                                buttonText="Add District"
                                buttonOnClick={onAddDistrict}
                                ariaLabel={`Add district to ${region.name}`}
                            />
                        </>
                    )}
                </div>
            )}
        </Card>
    );
};
